import { queryClient as defaultQueryClient } from '@/plugins/vueQuery';
import type { QueryInvalidationParams } from '@/types/query';
import { queryKeys } from '@/utils/constants';
import { getAgreementAthletesQueryKey } from '@/utils/queries/agreements';

const MODULE_KEY = 'athletes';

/** Query Key Getters */

/**
 * Cache key for the team athletes list (`getTeamAthletesList` API).
 *
 * The list query is keyed with several volatile trailing params (page size,
 * cursor, search, academic year, trashed, sort). This getter owns the stable
 * prefix `['athletes', 'getTeamAthletesList', <teamTwrn>]`; read sites append
 * their pagination/sort params after it. Invalidations only ever need the
 * prefix, so pass fewer args to invalidate a broader slice of the cache.
 */
export const getTeamAthletesListQueryKey = (teamTwrn?: string | null) => {
  const key = [MODULE_KEY, queryKeys.TEAM_ATHLETES_LIST];

  if (teamTwrn) {
    key.push(teamTwrn);
  }

  return key;
};

/**
 * Cache key for a single athlete's team profile (`getTeamAthleteProfile` API).
 * Fully encodable: `['athletes', 'getTeamAthleteProfile', <teamTwrn>, <athleteTwrn>]`.
 */
export const getTeamAthleteProfileQueryKey = (
  teamTwrn?: string | null,
  athleteTwrn?: string | null,
) => {
  const key = [MODULE_KEY, queryKeys.TEAM_ATHLETE_PROFILE];

  if (teamTwrn) {
    key.push(teamTwrn);
  }

  if (athleteTwrn) {
    if (!teamTwrn) {
      console.warn(
        '[getTeamAthleteProfileQueryKey] athleteTwrn was provided without teamTwrn. teamTwrn is required when athleteTwrn is passed.',
        {
          teamTwrn,
          athleteTwrn,
        },
      );
      return key;
    }
    key.push(athleteTwrn);
  }

  return key;
};

/**
 * Cache key for the eligible athletes list (`getEligibleAthletes` API).
 *
 * Eligible athletes are read with several different trailing shapes across the
 * app (a filter params object, a budget twrn, or a multi-budget marker). This
 * getter owns only the stable prefix
 * `['athletes', 'getEligibleAthletes', <teamTwrn>, <budgetTwrn>]`; read sites
 * append their filter object / mode after it. That keeps a single reliable
 * prefix for invalidation while leaving each read's exact key intact.
 */
export const getEligibleAthletesQueryKey = (
  teamTwrn?: string | null,
  budgetTwrn?: string | null,
) => {
  const key = [MODULE_KEY, queryKeys.ELIGIBLE_ATHLETES];

  if (teamTwrn) {
    key.push(teamTwrn);
  }

  if (budgetTwrn) {
    if (!teamTwrn) {
      console.warn(
        '[getEligibleAthletesQueryKey] budgetTwrn was provided without teamTwrn. teamTwrn is required when budgetTwrn is passed.',
        {
          teamTwrn,
          budgetTwrn,
        },
      );
      return key;
    }
    key.push(budgetTwrn);
  }

  return key;
};

/**
 * Cache key for an athlete's documents (`getAthleteDocuments` API).
 * Fully encodable: `['athletes', 'getAthleteDocuments', <teamTwrn>, <athleteTwrn>]`.
 *
 * Canonical order is team-scoped (teamTwrn before athleteTwrn), matching other
 * getters in this module. Call sites that previously used athlete-first order
 * must migrate to this shape.
 */
export const getAthleteDocumentsQueryKey = (
  teamTwrn?: string | null,
  athleteTwrn?: string | null,
) => {
  const key = [MODULE_KEY, queryKeys.ATHLETE_DOCUMENTS];

  if (teamTwrn) {
    key.push(teamTwrn);
  }

  if (athleteTwrn) {
    if (!teamTwrn) {
      console.warn(
        '[getAthleteDocumentsQueryKey] athleteTwrn was provided without teamTwrn. teamTwrn is required when athleteTwrn is passed.',
        {
          teamTwrn,
          athleteTwrn,
        },
      );
      return key;
    }
    key.push(athleteTwrn);
  }

  return key;
};

/** Invalidation Helpers */

export const invalidateAllAthletes = (options?: QueryInvalidationParams) => {
  const client = options?.queryClient ?? defaultQueryClient;

  client.invalidateQueries({ queryKey: getTeamAthletesListQueryKey() });
  client.invalidateQueries({ queryKey: getTeamAthleteProfileQueryKey() });
  client.invalidateQueries({ queryKey: getEligibleAthletesQueryKey() });
  client.invalidateQueries({ queryKey: getAgreementAthletesQueryKey() });
};
